<script setup lang="ts">
import { ref } from "vue";
import {
  useLoadImage,
  Timeline,
  ImageDisplayMode,
  computeImageDisplayRect,
  getViewport,
  Easing
} from "@zyzgroup/core-web";
import Pop from "../pop/Pop.vue";
import Box from "../base/Box.vue";

// for merge ce shadow-root styles
defineOptions({
  name: "Image",
  customOptions: { Pop },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    src: string;
    mode?: ImageDisplayMode;
    dur?: number;
    bgColor?: string;
    openImageDetailWhenClick?: boolean;
  }>(),
  {
    mode: "scaleToFill",
    dur: 300,
    bgColor: "transparent",
    openImageDetailWhenClick: true
  }
);

const emits = defineEmits<{
  (event: "mount", rect: DOMRect): void;
}>();

const popRef = ref();
let isOpenPop = false;

const imageRef = ref();

const tl = new Timeline({
  frameDuration: props.dur
});

// 所占空间
let containerWidth = 0;
let containerHeight = 0;
// 图片原始
let sourceWidth = 0;
let sourceHeight = 0;
// 图片显示
const showWidth = ref(0);
const showHeight = ref(0);
// pop最终位置
let finalX = 0;
let finalY = 0;
let finalWidth = 0;
let finalHeight = 0;
// pop动态位置
const popX = ref(0);
const popY = ref(0);
const popWidth = ref(0);
const popHeight = ref(0);

// const isCORS = ref(false);

const outterResize = (box) => {
  containerWidth = box.rect.width;
  containerHeight = box.rect.height;
  if (!sourceWidth || !sourceHeight) {
    // 初始化获取图片信息
    useLoadImage(props.src)
      .then((info: { width: number; height: number }) => {
        // isCORS.value = false;
        sourceWidth = info.width;
        sourceHeight = info.height;
      })
      .catch(() => {
        // isCORS.value = true;
        sourceWidth = containerWidth;
        sourceHeight = containerHeight;
      })
      .then(() => {
        setImageWidthHeight();
      });
  } else {
    setImageWidthHeight();
  }
};

const innerResize = (box) => {
  if (box.rect.width || box.rect.height) {
    emits("mount", box.rect);
  }
};

function setImageWidthHeight() {
  if (!containerWidth || !containerHeight || !sourceWidth || !sourceHeight) {
    return;
  }
  const wrapImageRect = computeImageDisplayRect(
    props.mode,
    sourceWidth,
    sourceHeight,
    containerWidth,
    containerHeight
  );
  showWidth.value = wrapImageRect.width;
  showHeight.value = wrapImageRect.height;
  const { width: viewportWidth, height: viewportHeight } = getViewport();
  const viewportImageRect = computeImageDisplayRect(
    "aspectFit",
    sourceWidth,
    sourceHeight,
    viewportWidth,
    viewportHeight
  );
  finalWidth = viewportImageRect.width;
  finalHeight = viewportImageRect.height;
  finalX = (viewportWidth - finalWidth) / 2;
  finalY = (viewportHeight - finalHeight) / 2;
}

function click() {
  if (!props.openImageDetailWhenClick) {
    return;
  }
  const rect = imageRef.value.getRect();
  tl.clear();
  tl.addTween(
    {
      left: rect.x,
      top: rect.y,
      width: showWidth.value,
      height: showHeight.value
    },
    {
      left: finalX,
      top: finalY,
      width: finalWidth,
      height: finalHeight
    },
    {
      easeFunction: Easing.Quadratic.Out,
      onUpdate(data) {
        popX.value = data.left;
        popY.value = data.top;
        popWidth.value = data.width;
        popHeight.value = data.height;
      },
      onComplete() {
        if (isOpenPop) {
          popRef.value?.close();
        }
        isOpenPop = !isOpenPop;
      }
    }
  );
  if (!isOpenPop) {
    popRef.value?.open();
    tl.play();
  }
}

function clickOverlay() {
  if (isOpenPop) {
    tl.reverse();
  }
}

defineExpose({});
</script>

<template>
  <Box
    class="image"
    display="full"
    :style="{
      backgroundColor: props.bgColor
    }"
    @resize="outterResize"
    @throttled-click="click"
  >
    <!-- <Box
      v-if="!isCORS"
      display="shrink"
      @resize="innerResize"
    >
      <img
        :src="props.src"
        :style="{
          display: 'block',
          objectFit: 'contain',
          cursor: props.openImageDetailWhenClick ? 'pointer' : 'default'
        }"
      />
    </Box> -->
    <!-- v-if="isCORS" -->
    <Box
      ref="imageRef"
      class="image-inner"
      display="shrink"
      :style="{
        width: showWidth + 'px',
        height: showHeight + 'px',
        backgroundImage: `url(${props.src})`,
        backgroundSize: `${showWidth}px ${showHeight}px`,
        cursor: props.openImageDetailWhenClick ? 'pointer' : 'default'
      }"
      @resize="innerResize"
    ></Box>
    <Pop
      ref="popRef"
      :direction-from="{ left: `${popX}px`, top: `${popY}px` }"
      :close-when-click-overlay="false"
      @clickOverlay="clickOverlay"
    >
      <div
        class="image-pop"
        :style="{
          width: popWidth + 'px',
          height: popHeight + 'px',
          backgroundImage: `url(${props.src})`,
          backgroundSize: '100% 100%'
        }"
      ></div>
    </Pop>
  </Box>
</template>

<style scoped>
.image {
  overflow: hidden;
  display: flex !important;
  justify-content: center;
  align-items: center;
}
.image-inner,
.image-pop {
  background-repeat: no-repeat;
  background-position: center center;
  /* background-size: contain; */
}
/*
.none {
  object-fit: none;
}
.fill {
  object-fit: fill;
}
.cover {
  object-fit: cover;
}
.contain {
  object-fit: contain;
}
.scale-down {
  object-fit: scale-down;
}
*/
</style>
